import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useToast } from '@/hooks/useToast';

// API Configuration - will be set after deploying the worker
const API_BASE_URL = 'https://regencommunity-api.your-subdomain.workers.dev/api';

export type ResourceCategory = 
	| 'food'
	| 'shelter'
	| 'medical'
	| 'skills'
	| 'tools'
	| 'transport'
	| 'legal'
	| 'childcare'
	| 'eldercare'
	| 'mental-health'
	| 'community'
	| 'other';

export interface Resource {
	id: string;
	name: string;
	category: ResourceCategory;
	description: string;
	address: string;
	latitude: number;
	longitude: number;
	contact?: {
		phone?: string;
		email?: string;
		website?: string;
	};
	hours?: string;
	verified: boolean;
	addedBy: string;
	addedAt: number;
	tags: string[];
}

interface ResourceContextType {
	resources: Resource[];
	addResource: (resource: Omit<Resource, 'id' | 'verified' | 'addedBy' | 'addedAt'>) => void;
	updateResource: (id: string, updates: Partial<Resource>) => void;
	deleteResource: (id: string) => void;
	verifyResource: (id: string) => void;
	filteredResources: Resource[];
	setFilters: (filters: ResourceFilters) => void;
	searchQuery: string;
	setSearchQuery: (query: string) => void;
	categoryFilters: ResourceCategory[];
	setCategoryFilters: (categories: ResourceCategory[]) => void;
	isLoading: boolean;
}

export interface ResourceFilters {
	category?: ResourceCategory[];
	verified?: boolean;
	search?: string;
}

const ResourceContext = createContext<ResourceContextType | null>(null);

// Sample data for demo
const sampleResources: Resource[] = [
	{
		id: '1',
		name: 'Garden Ninjas Community Garden',
		category: 'community',
		description: 'A shared garden space where neighbors grow vegetables and herbs together. All are welcome to contribute and harvest fresh produce.',
		address: 'Mangamanihi Rd, Kāeo',
		latitude: -34.8069,
		longitude: 173.9185,
		verified: true,
		addedBy: 'community',
		addedAt: Date.now() - 86400000 * 7,
		tags: ['garden', 'vegetables', 'organic'],
		contact: {
			email: 'gardenninjaskaeo@gmail.com',
			website: 'https://www.facebook.com/share/17DnLhjh5u/',
		},
	},
	{
		id: '2',
		name: 'Kāeo Library',
		category: 'skills',
		description: 'A community library offering books, workshops, and learning resources for all ages. Join our reading groups and skill-sharing sessions.',
		address: '30 Leigh Street, Kāeo',
		latitude: -34.8090,
		longitude: 173.9200,
		verified: true,
		addedBy: 'community',
		addedAt: Date.now() - 86400000 * 3,
		tags: ['books', 'learning', 'workshops', 'community'],
	},
	{
		id: '3',
		name: 'Garden Ninjas Community Garden',
		category: 'community',
		description: 'A shared garden space where neighbors grow vegetables and herbs together. All are welcome to contribute and harvest fresh produce.',
		address: 'Pupuke Road, Kāeo',
		latitude: -34.8100,
		longitude: 173.9150,
		verified: true,
		addedBy: 'community',
		addedAt: Date.now() - 86400000 * 5,
		tags: ['garden', 'vegetables', 'organic'],
		contact: {
			email: 'gardenninjaskaeo@gmail.com',
			website: 'https://www.facebook.com/share/17DnLhjh5u/',
		},
	},
	{
		id: '4',
		name: 'Eco Hub',
		category: 'community',
		description: 'A community space for meetings, workshops, and crop swaps. Join neighbors to share skills, seeds, and strengthen our local network.',
		address: 'Waikare Avenue, Kāeo',
		latitude: -34.8075,
		longitude: 173.9210,
		verified: true,
		addedBy: 'community',
		addedAt: Date.now() - 86400000 * 2,
		tags: ['meeting', 'workshop', 'community', 'cropswap'],
	},
	{
		id: '5',
		name: 'Te Pokapū Hauora Hub',
		category: 'medical',
		description: 'Community hub supporting Tinana, Hinengaro, and Wairua. Nau mai, haere mai — come as you are, rest where you need, and leave carrying less than you arrived with.',
		address: '21 Leigh Street, Kāeo',
		latitude: -34.8085,
		longitude: 173.9195,
		verified: true,
		addedBy: 'community',
		addedAt: Date.now() - 86400000 * 1,
		tags: ['Romiromi', 'Mirimiri', 'Hauora', 'TePokapū', 'HauoraWhangaroa'],
		contact: {
			website: 'https://www.facebook.com/share/1GqAh5zgVF/',
		},
	},
	{
		id: '6',
		name: 'Kāeo Crop Swap',
		category: 'food',
		description: 'Monthly crop swap meetup. Bring your surplus harvest and leave with fresh local produce. All welcome — no membership required.',
		address: 'Eco Hub, Waikare Avenue, Kāeo',
		latitude: -34.8075,
		longitude: 173.9210,
		hours: '1st Sunday of Month, 3pm-4.30pm',
		verified: true,
		addedBy: 'community',
		addedAt: Date.now(),
		tags: ['cropswap', 'food', 'garden', 'community', 'organic'],
		contact: {
			website: 'https://www.facebook.com/share/g/1L7zM3XznX/',
		},
	},
	{
		id: '8',
		name: 'Pātaka Kai / Food Pantry',
		category: 'food',
		description: 'Open Street Pantry Movement — a resident-led, grassroots solution to immediate local need. Take what you need, leave what you can. Neighbours and local businesses are welcome to donate non-perishables, fruit & veg from your gardens. Open 24/7. "Nā tō rourou, nā taku rourou ka ora ai te iwi" — With your food basket and my food basket the people will thrive.',
		address: 'Kāeo Farm & Fuel, 9 Leigh Street, Kāeo',
		latitude: -34.8090,
		longitude: 173.9200,
		hours: '24/7',
		verified: true,
		addedBy: 'community',
		addedAt: Date.now(),
		tags: ['community', 'free', 'food-give', 'pantry', 'food-rescue'],
	},
	{
		id: '7',
		name: 'Compassion Room',
		category: 'mental-health',
		description: 'Local EMOTIONAL SUPPORT for all. In-person, koha/donation. A safe, supportive space where you can be heard, seen, and heal. Be welcome in a space where you can be real, listened to and cared for.',
		address: 'Wainui Road, Kāeo',
		latitude: -34.8120,
		longitude: 173.9250,
		hours: 'By appointment',
		verified: true,
		addedBy: 'community',
		addedAt: Date.now(),
		tags: ['emotional-support', 'listening', 'healing', 'compassion', 'wellbeing', 'mental-health'],
		contact: {
			website: 'https://www.facebook.com/share/1BujRByiKB/',
		},
	},
];

// Fetch resources from API
async function fetchResourcesFromAPI(): Promise<Resource[]> {
	try {
		const response = await fetch(`${API_BASE_URL}/resources`);
		if (!response.ok) {
			throw new Error('Failed to fetch resources');
		}
		const data = await response.json();
		return data as Resource[];
	} catch (error) {
		console.warn('API unavailable, using sample data:', error);
		return sampleResources;
	}
}

// Save resource to API
async function saveResourceToAPI(resource: Resource): Promise<Resource> {
	const response = await fetch(`${API_BASE_URL}/resources`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(resource),
	});
	
	if (!response.ok) {
		throw new Error('Failed to save resource');
	}
	
	return response.json();
}

// Update resource in API
async function updateResourceInAPI(id: string, updates: Partial<Resource>): Promise<Resource> {
	const response = await fetch(`${API_BASE_URL}/resources/${id}`, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(updates),
	});
	
	if (!response.ok) {
		throw new Error('Failed to update resource');
	}
	
	return response.json();
}

// Delete resource from API
async function deleteResourceFromAPI(id: string): Promise<void> {
	const response = await fetch(`${API_BASE_URL}/resources/${id}`, {
		method: 'DELETE',
	});
	
	if (!response.ok) {
		throw new Error('Failed to delete resource');
	}
}

export function ResourceProvider({ children }: { children: ReactNode }) {
	const [resources, setResources] = useState<Resource[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [categoryFilters, setCategoryFilters] = useState<ResourceCategory[]>([]);
	const { toast } = useToast();
	
	// Load resources on mount
	useEffect(() => {
		async function loadResources() {
			setIsLoading(true);
			const data = await fetchResourcesFromAPI();
			setResources(data);
			setIsLoading(false);
		}
		
		loadResources();
	}, []);
	
	const addResource = useCallback(async (resource: Omit<Resource, 'id' | 'verified' | 'addedBy' | 'addedAt'>) => {
		const newResource: Resource = {
			...resource,
			id: crypto.randomUUID(),
			verified: false,
			addedBy: 'anonymous',
			addedAt: Date.now(),
		};
		
		// Optimistically update UI
		setResources(prev => [newResource, ...prev]);
		
		try {
			// Try to save to API
			await saveResourceToAPI(newResource);
		} catch (error) {
			console.warn('Could not save to API, using local fallback:', error);
		}
		
		toast({
			title: 'Resource added!',
			description: 'Thank you for helping your community.',
			variant: 'default',
		});
	}, [toast]);
	
	const updateResource = useCallback(async (id: string, updates: Partial<Resource>) => {
		// Optimistically update UI
		setResources(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
		
		try {
			await updateResourceInAPI(id, updates);
		} catch (error) {
			console.warn('Could not update in API:', error);
		}
		
		toast({
			title: 'Resource updated',
			description: 'Changes have been saved.',
		});
	}, [toast]);
	
	const deleteResource = useCallback(async (id: string) => {
		// Optimistically update UI
		setResources(prev => prev.filter(r => r.id !== id));
		
		try {
			await deleteResourceFromAPI(id);
		} catch (error) {
			console.warn('Could not delete from API:', error);
		}
		
		toast({
			title: 'Resource removed',
			description: 'The resource has been deleted.',
		});
	}, [toast]);
	
	const verifyResource = useCallback(async (id: string) => {
		await updateResource(id, { verified: true });
	}, [updateResource]);
	
	const filteredResources = resources.filter(resource => {
		// Category filter
		if (categoryFilters.length > 0 && !categoryFilters.includes(resource.category)) {
			return false;
		}
		
		// Search filter
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			return (
				resource.name.toLowerCase().includes(query) ||
				resource.description.toLowerCase().includes(query) ||
				resource.tags.some(tag => tag.toLowerCase().includes(query))
			);
		}
		
		return true;
	});
	
	const value: ResourceContextType = {
		resources,
		addResource,
		updateResource,
		deleteResource,
		verifyResource,
		filteredResources,
		setFilters: () => {},
		searchQuery,
		setSearchQuery,
		categoryFilters,
		setCategoryFilters,
		isLoading,
	};
	
	return (
		<ResourceContext.Provider value={value}>
			{children}
		</ResourceContext.Provider>
	);
}

export function useResources() {
	const context = useContext(ResourceContext);
	if (!context) {
		throw new Error('useResources must be used within ResourceProvider');
	}
	return context;
}
